import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ApiContext, type ApiContextValue } from './ApiContext';
import { ApiHandler } from './handlers';

type ApiProviderProps = {
    baseUrl: string;
    getAccessToken?: () => string | null;
    removeAccessToken?: () => void;
    children: React.ReactNode;
};

export const ApiProvider: React.FC<ApiProviderProps> = ({
    baseUrl,
    getAccessToken,
    removeAccessToken,
    children,
}) => {
    const [queryClient] = React.useState(() => new QueryClient());

    // `ApiHandler` reads `baseUrl` once at construction and threads it down to
    // every resource service. Re-create when `baseUrl` changes so a runtime
    // env swap (e.g. tests) doesn't keep the stale instance.
    const apiClient = React.useMemo(
        () => new ApiHandler({ baseUrl }),
        [baseUrl],
    );

    const value = React.useMemo<ApiContextValue>(
        () => ({
            apiClient,
            baseUrl,
            getAccessToken,
            removeAccessToken,
        }),
        [apiClient, baseUrl, getAccessToken, removeAccessToken],
    );

    return (
        <QueryClientProvider client={queryClient}>
            <ApiContext.Provider value={value}>{children}</ApiContext.Provider>
        </QueryClientProvider>
    );
};
